// src/components/blog/BlogPost.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`/api/blog/${slug}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Blog post not found');
          }
          throw new Error('Failed to fetch blog post');
        }
        
        const data = await response.json();
        setPost(data);
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError(err.message || 'Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPost();
  }, [slug]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-gray-900 py-8 text-gray-100 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <p className="text-gray-400 mt-2">Loading post...</p>
          </div>
        ) : error ? (
          <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 text-red-200 mb-6">
            <p>{error}</p>
            <Link to="/blog" className="inline-block mt-4 text-blue-400 hover:text-blue-300">
              ← Back to all posts
            </Link>
          </div>
        ) : post ? (
          <>
            <Link to="/blog" className="inline-block mb-6 text-blue-400 hover:text-blue-300">
              ← Back to all posts
            </Link>
            
            <h1 className="text-4xl font-bold mb-4 text-blue-400">{post.title}</h1>
            
            <div className="mb-8 text-gray-400">
              <p>
                Published on {formatDate(post.created_at)} • By {post.author || 'Admin'}
              </p>
            </div>
            
            {post.featured_image && (
              <div className="mb-8 rounded-lg overflow-hidden">
                <img 
                  src={`/media/blog/${post.featured_image}`} 
                  alt={post.title}
                  className="w-full h-auto object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/media/placeholder.gif";
                  }}
                />
              </div>
            )}
            
            <div className="prose prose-lg prose-invert max-w-none mb-8">
              <div dangerouslySetInnerHTML={{ __html: post.content }} />
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-xl font-semibold text-gray-400">Post not found</p>
            <Link to="/blog" className="inline-block mt-4 text-blue-400 hover:text-blue-300">
              ← Back to all posts
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPost;
